import { create } from 'zustand';
import type { RobotState } from '@fabricos/protocol-ts';
import { distMeters } from '@/store/gotoTargets';

/**
 * Per-robot flight session totals: time and ground track distance, counted
 * from ARM to DISARM (the standard GCS flight timer). While armed the
 * numbers tick live; after disarm they freeze so the operator can read the
 * completed flight's totals until the next arm resets them.
 *
 * Fed by the WS state flush in `useHubSocket` (same cadence as trails).
 * Segments under NOISE_M are ignored so GPS jitter on the ground doesn't
 * accumulate phantom metres.
 */
export interface FlightSession {
  /** Arm timestamp of the CURRENT (or last, if !active) flight. */
  armedAtMs: number;
  /** Frozen at disarm; while active compute live from armedAtMs. */
  flightMs: number;
  /** Ground-track distance integrated ONLY while airborne. */
  distanceM: number;
  /** Takeoff position — first airborne fix of the session. Fallback "home"
   *  for the displacement readout when the firmware reports none. */
  takeoffPos?: { lat: number; lon: number };
  active: boolean;
}

const NOISE_M = 0.5;

interface FlightStatsStore {
  sessions: Record<string, FlightSession>;
  ingest: (robotId: string, state: RobotState) => void;
}

/** Last integrated position per robot — module-level, not reactive state. */
const lastPos = new Map<string, { lat: number; lon: number }>();

export const useFlightStats = create<FlightStatsStore>((set, get) => ({
  sessions: {},

  ingest: (robotId, state) => {
    const armed = state.arm === 'armed';
    const cur = get().sessions[robotId];

    if (armed && !cur?.active) {
      lastPos.delete(robotId);
      if (state.position) lastPos.set(robotId, state.position);
      set((s) => ({
        sessions: {
          ...s.sessions,
          [robotId]: { armedAtMs: Date.now(), flightMs: 0, distanceM: 0, active: true },
        },
      }));
      return;
    }
    if (!cur) return;

    if (armed && cur.active) {
      const pos = state.position;
      if (!pos) return;
      // Distance counts AIR ONLY (operator ask): track (but don't
      // accumulate) while on the ground so liftoff doesn't inherit a jump.
      const airborne = (pos.alt_rel_home_m ?? 0) > 0.5;
      const prev = lastPos.get(robotId);
      lastPos.set(robotId, pos);
      if (!airborne || !prev) return;
      const patch: Partial<FlightSession> = {};
      if (!cur.takeoffPos) patch.takeoffPos = { lat: pos.lat, lon: pos.lon };
      const d = distMeters(prev, pos);
      if (d >= NOISE_M) patch.distanceM = cur.distanceM + d;
      if (Object.keys(patch).length === 0) return;
      set((s) => ({
        sessions: { ...s.sessions, [robotId]: { ...cur, ...patch } },
      }));
      return;
    }

    if (!armed && cur.active) {
      // Disarm — freeze the totals for post-flight reading.
      set((s) => ({
        sessions: {
          ...s.sessions,
          [robotId]: { ...cur, active: false, flightMs: Date.now() - cur.armedAtMs },
        },
      }));
    }
  },
}));

/** Live flight time in ms — ticking while armed, frozen after disarm. */
export function flightTimeMs(s: FlightSession): number {
  return s.active ? Date.now() - s.armedAtMs : s.flightMs;
}

/** "MM:SS" (or "H:MM:SS" past the hour). */
export function fmtFlightTime(ms: number): string {
  const totalS = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalS / 3600);
  const mm = Math.floor((totalS % 3600) / 60);
  const ss = totalS % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
}
